import { AgGridReact } from "ag-grid-react";
import { AxiosResponse } from "axios";
import React, { ReactElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuRefreshCcw } from "react-icons/lu";
import { MdOutlineFileDownload } from "react-icons/md";
import { SiConvertio } from "react-icons/si";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

import useFetch from "../../../hooks/useFetchMSAL";
import useInput from "../../../hooks/useInput";
import { ReplenishmentPlanTabFetchAPI } from "../../../models/APIResponses.model";
import CalculatedSupplyReceipt from "../../../models/ReplenishmentPlan/CalculatedSupplyReceipt.model";
import ReplenishmentPlanSummary from "../../../models/ReplenishmentPlan/ReplenishmentPlanSummary.model";
import { AG_GRID_DEFAULT_COL_DEF, AG_GRID_MODULES, AG_GRID_OPTIONS, CALCULATED_SUPPLY_RECEIPTS_TAB_ID, TOAST_CONTAINER_ID } from "../../../shared/constants";
import { usaNumberFormatter, getToastOptions } from "../../../shared/functions";
import { RootState } from "../../../store";
import { replenishmentTabActions } from "../../../store/slices/replenishment-tab-slice";
import "../../../styles.css";
import Modal from "../../UI/Modal/Modal";
import "./CalculatedSupplyReceiptsGrid.css";

const CalculatedSupplyReceiptsGrid = (props: {
    gridHeight: string;
}) => {
    const [, fetchData] = useFetch([]);
    // States
    const [gridRowData, setGridRowData] = useState<CalculatedSupplyReceipt[]>([]);
    const [selectedRows, setSelectedRows] = useState<any>([]);
    
    // Refs
    const modalCloseBtnRef = useRef<HTMLButtonElement>(null);
    const gridRef = useRef<AgGridReact>(null);

    // Variables
    const dispatchFn = useDispatch();
    const tabApiDataFetched: boolean = useSelector((state: RootState) => state.replenishmentTab.tabApiDataFetched)[CALCULATED_SUPPLY_RECEIPTS_TAB_ID];
    const activeSubTabId: string = useSelector((state: RootState) => state.replenishmentTab.activeSubTabId);
    const selectedSlicers = useSelector((state: RootState) => state.replenishmentTab.selectedSlicers);
    const isSiteMasterUpdated = useSelector((state: RootState) => state.masterTables.isSiteMasterUpdated);
    const isPartMasterUpdated = useSelector((state: RootState) => state.masterTables.isPartMasterUpdated);
    const regeneratedFromCSR = useSelector((state: RootState) => state.replenishmentTab.regeneratedFromCSR);
    const convertOrderModalId: string = "convertToFirmOrderModal";
    let rowIdList: any[] = [];
    const gridColDef: any[] = [
        {
            field: "site",
            tooltipField: "site",
            headerTooltip: "Site Name",
            cellDataType: "text",
            headerName: "Site Name",
        },
        {
            field: "sku",
            tooltipField: "sku",
            headerTooltip: "SKU Code",
            cellDataType: "text",
            headerName: "SKU Code",
        },
        {
            field: "supply_id",
            tooltipField: "supply_id",
            headerTooltip: "Supply ID",
            cellDataType: "text",
            headerName: "Supply ID",
        },
        {
            field: "projected_release_date",
            tooltipField: "projected_release_date",
            headerTooltip: "Proj. Release Date",
            cellDataType: "text",
            headerName: "Proj. Release Date",
        },
        {
            field: "expected_receipt_date",
            tooltipField: "expected_receipt_date",
            headerTooltip: "Expected Receipt Date",
            cellDataType: "text",
            headerName: "Expected Receipt Date",
        },
        {
            field: "quantity",
            headerTooltip: "Qty",
            cellDataType: "number",
            cellClass: "ag-right-aligned-cell",
            headerClass: "ag-left-aligned-cell",
            headerName: "Qty",
            tooltipValueGetter: (params: any) => usaNumberFormatter(params.data.quantity, 2),
            valueFormatter: (params: any) => usaNumberFormatter(params.data.quantity, 2)
        },
        {
            field: "status",
            tooltipField: "status",
            headerTooltip: "Status",
            cellDataType: "text",
            headerName: "Status",
        },
        {
            headerName: "#",
            headerCheckboxSelection: true,
            checkboxSelection: true,
            showDisabledCheckboxes: true,
        }
    ];

    const fetchCalculatedSupplyReceipts = useCallback(async () => {
        const fetchingDataToastID = toast.loading('Fetching calculated supply receipts data...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const fetchAPIResponse: AxiosResponse<ReplenishmentPlanTabFetchAPI<CalculatedSupplyReceipt[]>> =
                await fetchData(`/calculated-supply-receipts`, {
                    method: 'GET',
                    params: {
                        site_code: selectedSlicers.site_code,
                        part_code: selectedSlicers.part_code,
                        number_of_weeks: selectedSlicers.number_of_weeks
                    }
                });

            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });

            if (fetchAPIResponse.data.data) {
                setGridRowData(fetchAPIResponse.data.data.result);
                dispatchFn(replenishmentTabActions.setSlicers(fetchAPIResponse.data.data.slicers));
                dispatchFn(replenishmentTabActions.setTabApiDataFetched({
                    [CALCULATED_SUPPLY_RECEIPTS_TAB_ID]: true
                }));

            } else {
                toast.error('Error in fetching calculated supply receipts data', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }

        } catch (error: Error | any) {
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: fetchingDataToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in fetching data'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    }, [dispatchFn, fetchData, selectedSlicers]);

    const modalFormSubmissionHandler = async (event: React.FormEvent) => {
        event.preventDefault();

        if (selectedRows.length === 0) {
            toast.error('Kindly select a supply receipt to convert to firm order.', {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
            return;
        }

        const convertingOrderToastID = toast.loading('Converting to firm order...', {
            containerId: TOAST_CONTAINER_ID,
            ...getToastOptions('loading')
        });

        try {
            const apiResponse: AxiosResponse<{
                data: string | null;
                error: string | null;
            }> =
                await fetchData(`/convert-to-firm-order`, {
                    method: "POST",
                    data: {
                        selectedRows: selectedRows,
                        site_code: selectedSlicers.site_code,
                        part_code: selectedSlicers.part_code,
                        number_of_weeks: selectedSlicers.number_of_weeks
                    }
                });

            toast.dismiss({
                id: convertingOrderToastID,
                containerId: TOAST_CONTAINER_ID
            });

            if (apiResponse.data.data) {
                dispatchFn(replenishmentTabActions.setRegeneratedFromCSR(!regeneratedFromCSR));
                dispatchFn(replenishmentTabActions.setTabApiDataFetched({
                    [CALCULATED_SUPPLY_RECEIPTS_TAB_ID]: false
                }));

                modalCloseBtnRef.current?.click();

                toast.success(apiResponse.data.data, {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions("success")
                });

                // fetchCalculatedSupplyReceipts();
                // dispatchFn(replenishmentTabActions.setRefreshSummaryGrid(true));
                // dispatchFn(replenishmentTabActions.setTabApiDataFetched({
                //     [PLANNED_SUPPLY_RECEIPTS_TAB_ID]: false
                // }));

            } else {
                toast.error('Error in converting to firm order.', {
                    containerId: TOAST_CONTAINER_ID,
                    ...getToastOptions('error')
                });
            }

        } catch (error: Error | any) {
            console.error(`Request Error: ${error}`);
            toast.dismiss({
                id: convertingOrderToastID,
                containerId: TOAST_CONTAINER_ID
            });
            toast.error((
                error?.response?.data?.error ||
                'Error in converting to firm order.'
            ), {
                containerId: TOAST_CONTAINER_ID,
                ...getToastOptions('error')
            });
        }
    };

    const downloadReports = useCallback(() => {
        const gridApi = gridRef.current?.api;
        if (!gridApi) return;

        const originalColumnDefs = gridApi.getColumnDefs();

        // Update column definitions to hide 'Actions' column
        const updatedColumnDefs = originalColumnDefs?.map(colDef => {
            if (colDef.headerName === '#') {
                return { ...colDef, hide: true };
            }
            return colDef;
        });

        // Update grid options to apply changes
        gridApi.updateGridOptions({
            columnDefs: updatedColumnDefs,
        });

        // Retrieve all rows
        const allRows: any = [];
        gridApi.forEachNode(node => allRows.push(node.data));

        // Filter rows where the status column value is 'Calculated'
        const filteredRows = allRows.filter((row: any) => row.status === 'Calculated');

        // Temporarily set the grid data to filteredRows for export
        gridApi.updateGridOptions({
            rowData: filteredRows,
        });

        // Export data as Excel
        gridApi.exportDataAsExcel({
            sheetName: 'Filtered Data',
        });

        // Restore original data after export
        gridApi.updateGridOptions({
            rowData: gridRowData,
        });

    }, [gridRowData]);

    const onSelectionChanged = () => {
        const selectedRows1 = gridRef.current!.api!.getSelectedRows();
        selectedRows1.forEach((e) => {
            rowIdList.push(e.supply_id);
        });

        setSelectedRows(rowIdList);
        rowIdList = [];
    };

    const modalForm: ReactElement = (
        <form onSubmit={modalFormSubmissionHandler}>
            <div className="row">
                <p className="text-center py-2">Are you sure you want to make to firm?</p>
            </div>
            <div className="row">
                <div className="col col-6 mb-2 d-grid">
                </div>
                <div className="col col-6 mb-2 d-grid">
                    <button type="button" className="btn btn-warning" onClick={modalFormSubmissionHandler}>
                        <span className="fs-5">
                            <LuRefreshCcw />
                        </span> Convert to Firm
                    </button>
                </div>
            </div>
        </form>
    );

    // Initial run
    useEffect(() => {
        if (
            !tabApiDataFetched &&
            activeSubTabId === CALCULATED_SUPPLY_RECEIPTS_TAB_ID
        ) {
            fetchCalculatedSupplyReceipts();
        }

    }, [fetchCalculatedSupplyReceipts, fetchData, tabApiDataFetched, activeSubTabId, selectedSlicers, isPartMasterUpdated, isSiteMasterUpdated]);

    return (
        <>
            <div className="d-flex justify-content-end">
                <button className="btn btn-sm btn-custom-primary mx-2" disabled={selectedRows.length === 0} title="Convert to firm order" data-bs-toggle="modal" data-bs-target={`#${convertOrderModalId}`}>
                    <span className="fs-6" >
                        <SiConvertio />
                    </span> Convert
                </button>
                <button
                    title="Download Report"
                    className="btn btn-sm btn-custom-primary"
                    disabled={!gridRowData.length}
                    onClick={downloadReports}
                >
                    <MdOutlineFileDownload /> &nbsp;Download Report
                </button>
            </div>

            <div className="ag-theme-balham mt-2" style={{ height: props.gridHeight, maxHeight: props.gridHeight, width: '100%', overflowX: 'auto', overflowY: 'auto' }}>
                <AgGridReact
                    ref={gridRef}
                    rowData={gridRowData}
                    columnDefs={gridColDef}
                    gridOptions={{
                        rowHeight: 50,
                        onSelectionChanged: onSelectionChanged,
                        ...(AG_GRID_OPTIONS as any)
                    }}
                    defaultColDef={{
                        width: 100,
                        ...(AG_GRID_DEFAULT_COL_DEF as any)
                    }}
                    rowSelection={"multiple"}
                    modules={AG_GRID_MODULES}
                />
            </div>

            {
                createPortal(
                    <Modal
                        id={convertOrderModalId}
                        ref={modalCloseBtnRef}
                        title="Convert to Firm Order"
                        onModalClose={() => {}}
                    >
                        {modalForm}
                    </Modal>,
                    document.getElementById("modal-root") as HTMLElement
                )
            }
        </>
    );
};

export default CalculatedSupplyReceiptsGrid;
